// ABOUTME: Drag-to-reorder for one plain list — the list owns a monitor, each row its drag wiring
// ABOUTME: Self-contained: unlike the calendar's SortableItem it needs no DndProvider

import { useEffect, useEffectEvent, useState } from "react"
import { draggable, dropTargetForElements, monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { attachClosestEdge, extractClosestEdge, type Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge"
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine"

const LIST_KEY = "sortableListId"
const INDEX_KEY = "sortableIndex"
const GROUP_KEY = "sortableGroup"

const belongsTo = (data: Record<string, unknown>, listId: string) => data[LIST_KEY] === listId

/** Where a row sits: which container, and where inside it. */
export interface SortablePosition {
  /** The container a row belongs to — a meal, say. Undefined for a flat list. */
  group?: string | number
  index: number
}

/**
 * Where the dragged row lands, in the coordinates `move(from, to)` expects —
 * null when it would not actually move.
 *
 * Dropping below a row lands after it. Taking the dragged row out first shifts
 * every later position down by one — but only when it came from the same
 * container, which is why the group matters here.
 */
export const resolveReorderTarget = (
  from: SortablePosition,
  over: SortablePosition,
  edge: Edge | null,
): number | null => {
  let to = edge === "bottom" ? over.index + 1 : over.index
  const sameGroup = from.group === over.group
  if (sameGroup && from.index < to) to -= 1
  return sameGroup && to === from.index ? null : to
}

/**
 * Reordering of a plain array — a react-hook-form field array, say.
 *
 * The calendar's `SortableItem` cannot serve here: it reports to a DndProvider
 * through a typed `DndLocation`, and both only exist inside the calendar.
 *
 * `listId` is what a move may cross: rows that share one keep their moves to
 * themselves. Rows in different containers CAN share a list — then `group`
 * says which container each is in, and the callback gets both ends.
 */
export const useSortableList = (listId: string, onReorder: (from: SortablePosition, to: SortablePosition) => void) => {
  const reportReorder = useEffectEvent(onReorder)

  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => belongsTo(source.data, listId),
        onDrop: ({ source, location }) => {
          // Innermost first: a row of another list would come before its card,
          // and a payload that is not ours is simply not a reorder.
          const target = location.current.dropTargets[0]
          if (!target || !belongsTo(target.data, listId)) return

          const from: SortablePosition = {
            group: source.data[GROUP_KEY] as string | number | undefined,
            index: source.data[INDEX_KEY] as number,
          }
          const over: SortablePosition = {
            group: target.data[GROUP_KEY] as string | number | undefined,
            index: target.data[INDEX_KEY] as number,
          }

          const index = resolveReorderTarget(from, over, extractClosestEdge(target.data))
          if (index !== null) reportReorder(from, { group: over.group, index })
        },
      }),
    [listId],
  )
}

export interface SortableRow {
  /** The element that moves — attach to the row or card itself. */
  ref: (element: HTMLElement | null) => void
  /** The element that starts the drag — the grab handle, and only it. */
  dragHandleRef: (element: HTMLElement | null) => void
  isDragging: boolean
  /** Which side of this row the dragged one would land on, if any. */
  closestEdge: Edge | null
}

export const useSortableRow = (
  listId: string,
  index: number,
  isDisabled = false,
  /** Which container this row is in, when the list spans several. */
  group?: string | number,
): SortableRow => {
  // The elements live in state, not refs: a handle that mounts after its row (a menu opening, a column
  // appearing) sets state, and the wiring below runs again with both in hand.
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [dragHandle, setDragHandle] = useState<HTMLElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null)

  useEffect(() => {
    if (!element || !dragHandle || isDisabled) return

    const data = { [LIST_KEY]: listId, [INDEX_KEY]: index, [GROUP_KEY]: group }

    return combine(
      draggable({
        element,
        dragHandle,
        getInitialData: () => data,
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) =>
          belongsTo(source.data, listId) && !(source.data[INDEX_KEY] === index && source.data[GROUP_KEY] === group),
        getData: ({ input, element }) => attachClosestEdge(data, { input, element, allowedEdges: ["top", "bottom"] }),
        onDrag: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
        onDragLeave: () => setClosestEdge(null),
        onDrop: () => setClosestEdge(null),
      }),
    )
  }, [element, dragHandle, listId, index, isDisabled, group])

  return { ref: setElement, dragHandleRef: setDragHandle, isDragging, closestEdge }
}
